import { FC, Fragment } from 'react';
import { Button, InlineNotification, Link } from '@carbon/react';
import type { ReconcileCreateResponse } from 'types/api-responses';
import style from './party-detail.module.scss';

export const reconcileOutcomeLabel = (
  outcome: ReconcileCreateResponse['outcome']
) => {
  if (outcome === 'alias_only') return 'Entities linked as aliases';
  if (outcome === 'mixed') return 'Entities deduplicated and aliased';
  return 'Entities deduplicated';
};

// Comma-separated /entities links for a list of ids.
const entityIdLinks = (ids: number[]) => {
  if (ids.length === 0) return '—';
  return ids.map((id, i) => (
    <Fragment key={id}>
      {i > 0 && ', '}
      <Link href={`/entities/${id}`}>{id}</Link>
    </Fragment>
  ));
};

interface Props {
  result: ReconcileCreateResponse;
  // Owned by the parent so it survives the banner remounting around a refetch.
  detailsOpen: boolean;
  onToggleDetails: () => void;
}

export const ReconcileResultBanner: FC<Props> = ({
  result,
  detailsOpen,
  onToggleDetails,
}) => {
  const retired = result.retiredEntityIds.length;
  const aliases = result.aliasPairs.length;
  const droppedValues = result.lostAttributes.length > 0;

  return (
    <div className={style.reconcileResultBanner}>
      <InlineNotification
        kind={droppedValues ? 'warning' : 'success'}
        lowContrast
        hideCloseButton
        title={`${reconcileOutcomeLabel(result.outcome)} —`}
        subtitle={`${retired} entit${
          retired === 1 ? 'y' : 'ies'
        } retired, ${aliases} alias link${aliases === 1 ? '' : 's'} created${
          droppedValues
            ? '; some conflicting attribute values were dropped'
            : ''
        }.`}
        className={style.provisionalBanner}
      />
      <Button kind="ghost" size="sm" onClick={onToggleDetails}>
        {detailsOpen ? 'Hide details' : 'Show details'}
      </Button>
      {detailsOpen && (
        <dl className={style.reconcileResultDetails}>
          <div>
            <dt>Surviving entity IDs</dt>
            <dd>{entityIdLinks(result.survivingEntityIds)}</dd>
          </div>
          <div>
            <dt>Retired entity IDs</dt>
            <dd>{entityIdLinks(result.retiredEntityIds)}</dd>
          </div>
          {aliases > 0 && (
            <div>
              <dt>Aliased entity IDs</dt>
              <dd>
                {result.aliasPairs.map(([a, b], i) => (
                  <Fragment key={`${a}-${b}`}>
                    {i > 0 && ', '}
                    <Link href={`/entities/${a}`}>{a}</Link>
                    {' ↔ '}
                    <Link href={`/entities/${b}`}>{b}</Link>
                  </Fragment>
                ))}
              </dd>
            </div>
          )}
          {droppedValues && (
            <div>
              <dt>Dropped attribute values</dt>
              <dd>
                {result.lostAttributes
                  .map((l) => `${l.attribute} (kept ${String(l.kept ?? '—')})`)
                  .join(', ')}
              </dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
};
